import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminTenants } from '@/hooks/useAdminTenants'
import { useImpersonation } from '@/hooks/useImpersonation'

interface TenantPickerModalProps {
  targetModule: string
  targetPath: string
  onClose: () => void
}

export default function TenantPickerModal({ targetModule, targetPath, onClose }: TenantPickerModalProps) {
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const { impersonate, isStarting } = useImpersonation()
  const { data, isLoading } = useAdminTenants({ status: 'active', search: search || undefined })

  const handleSelect = async (tenantId: string) => {
    try {
      await impersonate(tenantId)
      navigate(targetPath)
      onClose()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start impersonation')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[70vh] flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Select tenant for {targetModule}
          </h3>
          <input
            type="text"
            placeholder="Search tenants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-personic-500 focus:border-transparent"
            autoFocus
          />
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {isLoading && <p className="p-4 text-gray-500 text-center">Loading...</p>}
          {data?.tenants.map(tenant => (
            <button
              key={tenant.id}
              onClick={() => handleSelect(tenant.id)}
              disabled={isStarting}
              className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <div className="font-medium text-gray-900">{tenant.name}</div>
              <div className="text-sm text-gray-500">{tenant.slug}</div>
            </button>
          ))}
          {!isLoading && data?.tenants.length === 0 && (
            <p className="p-4 text-gray-500 text-center">No active tenants found</p>
          )}
        </div>

        <div className="p-4 border-t border-gray-200">
          <button onClick={onClose} className="w-full btn btn-secondary text-sm">Cancel</button>
        </div>
      </div>
    </div>
  )
}
